import { useMutation, useQuery, useReactiveVar } from "@apollo/client";
import { LocationGenerics } from "router/location";
import { DisapproveServiceOrderForm } from "components/forms";
import { Modal, ConfirmationModal } from "components/layouts";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { useSearch } from "react-location";
import { DISAPPROVE_SERVICE_ORDER, GET_SERVICE_ORDER } from "./apollo";
import { useMemo, useState } from "react";
import { currentUserVar } from "apollo/cache/auth";

export default function DisapproveInspectionServiceOrderContainer({
  open,
  setOpen,
  refetch,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}) {
  const searchParams = useSearch<LocationGenerics>();
  const currentUser = useReactiveVar(currentUserVar);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const { data, loading } = useQuery(GET_SERVICE_ORDER, {
    variables: {
      id: searchParams.id,
    },
    notifyOnNetworkStatusChange: false,
  });
  const [disapproveServiceOrder] = useMutation(DISAPPROVE_SERVICE_ORDER);

  const canPerform = useMemo(
    () => data?.serviceOrder?.assignee?._id === currentUser?._id,
    [data?.serviceOrder?.assignee?._id, currentUser?._id],
  );

  const form = useFormik({
    initialValues: {
      reason: "",
    },
    onSubmit: async (values) => {
      await disapproveServiceOrder({
        variables: {
          id: searchParams.id,
          ...values,
        },
      }).then(({ data }) => {
        if (data.disapproveServiceOrder._id) {
          toast(
            JSON.stringify({
              type: "success",
              title: "Service Order Disapproved Successfully",
            })
          );
          refetch?.();
          form.resetForm();
        } else {
          toast(
            JSON.stringify({
              type: "error",
              title: "Could not disapprove Service Order",
            })
          );
        }
      });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  return (
    <>
      <Modal
        open={open}
        setOpen={setOpen}
        title="Disapprove Service Order"
        description="Provide reasons for disapproving service order"
        loading={loading}
        renderActions={() => (
          <>
            {canPerform && (
              <button
                type="button"
                disabled={form.isSubmitting}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={() => setIsConfirmationOpen(true)}
              >
                Disapprove Service Order
              </button>
            )}
          </>
        )}
      >
        {data?.serviceOrder?._id && <DisapproveServiceOrderForm form={form} />}
      </Modal>
      <ConfirmationModal
        open={isConfirmationOpen}
        disabled={form.isSubmitting}
        title={"Are you sure you want to disapprove this service order?"}
        okayText={form.isSubmitting ? "Disapproving..." : "Disapprove"}
        onCancel={() => setIsConfirmationOpen(false)}
        onOkay={form.handleSubmit}
      />
    </>
  );
}
